import { BooleanOperator, ComparisonOperator, FileSizeUnit } from '@common/enums';
import { getSize, getSizeMagnitude, SizeFilter } from '@common/services/smart-search/smart-search.helper';
import { FilterOptions } from '@common/types';
import { isValidDate, quoteODataEncoding } from '@common/utils';
import { SmartSearchModel, Template } from 'mbs-ui-kit';
import { Observable, of } from 'rxjs';
import { debounceTime, distinctUntilChanged, switchMap } from 'rxjs/operators';

export const containsWrapper = (prop: string, term: string): string => {
  return `contains(tolower(${prop}),'${quoteODataEncoding(term).toLowerCase()}')`;
};

export const getFilterByContains = (options: FilterOptions): string => {
  const { model, prop } = options;
  const term = Array.isArray(model) ? model.map((word) => word.value).join(' ') : '';

  return containsWrapper(prop, term);
};
export const getFilterByEq = (options: FilterOptions, quotes = true): string => {
  const { model, prop } = options;
  const term = Array.isArray(model) ? model.map((word) => word.value).join(' ') : '';

  return quotes ? `${prop} ${ComparisonOperator.Eq} '${term}'` : `${prop} ${ComparisonOperator.Eq} ${term}`;
};

export const getFilterByDate = (options: FilterOptions): string => {
  const { model, prop } = options;
  const term = Array.isArray(model) ? model.map((word) => word.value).join(' ') : '';
  const date = new Date(term);

  if (isValidDate(date)) {
    const formattedFrom = new Date(date.setHours(0, 0, 0, 0));
    const formattedTo = new Date(date.setHours(23, 59, 59, 999));

    return `${prop} ${ComparisonOperator.Gt} ${formattedFrom.toISOString()} ${BooleanOperator.And} ${prop} ${
      ComparisonOperator.Lt
    } ${formattedTo.toISOString()}`;
  }

  return `${prop} ${ComparisonOperator.Lt} '${term}' ${BooleanOperator.And} ${prop} ${ComparisonOperator.Gt} '${term}'`;
};

export const getFilterByDateRange = (options: FilterOptions, direction: 'from' | 'to'): string => {
  const { model, prop } = options;
  const term = Array.isArray(model) ? model.map((word) => word.value).join(' ') : '';
  const date = new Date(term);

  if (isValidDate(date)) {
    const formattedFrom = new Date(date.setHours(0, 0, 0, 0));
    const formattedTo = new Date(date.setHours(23, 59, 59, 999));

    switch (direction) {
      case 'from':
        return `${prop} ${ComparisonOperator.Gt} ${formattedFrom.toISOString()}`;
      case 'to':
        return `${prop} ${ComparisonOperator.Lt} ${formattedTo.toISOString()}`;
      default:
        return '';
    }
  }

  return '';
};

export const getFilterByEqFromEnum = (options: FilterOptions, quotes = true): string => {
  const { model, prop, enumItems } = options;
  const term = Array.isArray(model)
    ? model
        .map((word: { value: string }) => {
          const pair = Object.entries(enumItems).find(([, value]) => value.displayName.toLowerCase() === word.value.toLowerCase());

          return pair ? pair[0] : word.value;
        })
        .join('')
    : '';

  return quotes ? `${prop} ${ComparisonOperator.Eq} '${term}'` : `${prop} ${ComparisonOperator.Eq} ${term}`;
};

export const getFilterBySize = (options: FilterOptions & { filter: SizeFilter }): string => {
  const { model, prop, filter } = options;
  const term = Array.isArray(model) ? model.map((word) => word.value).join(' ') : '';
  const sign = filter == SizeFilter.Less ? ComparisonOperator.Lt : ComparisonOperator.Gt;
  const size = getSize(term);
  const magnitude = getSizeMagnitude(term);
  let bytes = +size;

  if (size && magnitude) {
    if (magnitude.toLowerCase() == FileSizeUnit.Gb.toLowerCase()) bytes = +size * 1024 * 1024 * 1024;
    if (magnitude.toLowerCase() == FileSizeUnit.Mb.toLowerCase()) bytes = +size * 1024 * 1024;
    if (magnitude.toLowerCase() == FileSizeUnit.Kb.toLowerCase()) bytes = +size * 1024;
  }

  return `${prop} ${sign} ${Math.trunc(bytes)}`;
};

export const filterByWords = (obj: SmartSearchModel): string => {
  return obj.words.map((word) => word.value).join(' ');
};

export function createTemplate(tag: string, getItems: (string) => Observable<any[]> = null): Template<any> {
  if (getItems == null) {
    getItems = () => of([]);
  }

  return {
    tag,
    items: (text$: Observable<string>) => {
      return text$.pipe(
        debounceTime(300),
        distinctUntilChanged(),
        switchMap((text) => getItems(text))
      );
    },
    itemFormatter: (value: string) => (typeof value === 'string' && value.split(/\s/).length > 1 ? `{${value.trim()}}` : value)
  };
}

export function firstOrDefault(arr: string[]): string | null {
  return arr?.length > 0 ? arr[0] : null;
}

export function smartSearchModelFactory(key: string, data: string | string[]): SmartSearchModel {
  if (!data) console.warn('Something wrong. There is no data to convert to search model. Look smartSearchModelFactory function');

  const values = Array.isArray(data) ? data : [data];

  return {
    [key]: values.map((value) => ({ value }))
  };
}

export function createFilterOptions(value: string, prop: string, condition?: string): FilterOptions {
  return {
    model: [
      {
        value,
        ...(condition ? { condition } : {})
      }
    ],
    prop
  };
}
